import React, { useState, useEffect } from 'react';
import { automationRulesApi, carriersApi, CarrierWithMethods } from '../services/api';

interface AutomationRule {
  id: string;
  name: string;
  description?: string;
  priority: number;
  enabled: boolean;
  conditions: any;
  actions: any;
  times_triggered: number;
  last_triggered_at?: string;
  created_at: string;
}

interface Condition {
  field: string;
  operator: string;
  value: any;
}

interface RuleActions {
  carrier?: string;
  service_code?: string;
  box_preset?: string;
  require_signature?: boolean;
  add_insurance?: boolean;
  insurance_provider?: 'parcelguard' | 'carrier';
  insurance_value_type?: 'order_total' | 'fixed';
  insurance_value?: number;
}

// Field definitions with labels and types
const FIELDS = [
  { value: 'order_weight', label: 'Order Weight (oz)', type: 'number' },
  { value: 'order_value', label: 'Order Value ($)', type: 'number' },
  { value: 'item_count', label: 'Item Count', type: 'number' },
  { value: 'shipping_country', label: 'Shipping Country', type: 'string' },
  { value: 'shipping_state', label: 'Shipping State', type: 'string' },
  { value: 'shipping_zip', label: 'Shipping ZIP', type: 'string' },
];

const OPERATORS = [
  { value: '=', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: '>', label: 'greater than' },
  { value: '<', label: 'less than' },
  { value: '>=', label: 'greater than or equal' },
  { value: '<=', label: 'less than or equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
];

const BOX_PRESETS = [
  { value: 'small_box', label: 'Small Box (12x9x3)' },
  { value: 'medium_box', label: 'Medium Box (16x12x6)' },
  { value: 'large_box', label: 'Large Box (20x16x10)' },
  { value: 'envelope', label: 'Envelope (12x9x0.5)' },
  { value: 'flat_rate_small', label: 'USPS Flat Rate Small' },
  { value: 'flat_rate_medium', label: 'USPS Flat Rate Medium' },
  { value: 'flat_rate_large', label: 'USPS Flat Rate Large' },
];

export const AutomationRules: React.FC = () => {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);
  const [reapplying, setReapplying] = useState(false);

  // Dynamic carriers from API
  const [carriers, setCarriers] = useState<CarrierWithMethods[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 0,
    enabled: true,
    conditionLogic: 'all' as 'all' | 'any',
    conditions: [{ field: 'order_weight', operator: '<', value: '' }] as Condition[],
    actions: {
      carrier: '',
      service_code: '',
      box_preset: '',
      require_signature: false,
      add_insurance: false,
      insurance_provider: 'parcelguard' as 'parcelguard' | 'carrier',
      insurance_value_type: 'order_total' as 'order_total' | 'fixed',
      insurance_value: 0,
    } as RuleActions,
  });

  useEffect(() => {
    loadRules();
    loadTemplates();
    loadCarriers();
  }, []);

  const loadCarriers = async () => {
    try {
      const data = await carriersApi.list();
      // Only show enabled carriers
      setCarriers((data.carriers || []).filter(c => c.enabled));
    } catch (error) {
      console.error('Failed to load carriers:', error);
    }
  };

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await automationRulesApi.list();
      setRules(data.rules || []);
    } catch (error) {
      console.error('Failed to load rules:', error);
      alert('Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await automationRulesApi.getTemplates();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleReapplyRules = async () => {
    if (!window.confirm(
      'Apply automation rules to all pending orders?\n\n' +
      'This will evaluate all enabled rules against orders awaiting shipment ' +
      'and update their settings (insurance, carrier preferences, etc.).\n\n' +
      'Continue?'
    )) {
      return;
    }

    setReapplying(true);
    try {
      const result = await automationRulesApi.reapplyToOrders();
      alert(`Done!\n\nProcessed ${result.processed} orders\nRules applied to ${result.rules_applied} orders`);
    } catch (error: any) {
      alert(`Failed to reapply rules: ${error.response?.data?.error || error.message}`);
    } finally {
      setReapplying(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      priority: rules.length * 10,
      enabled: true,
      conditionLogic: 'all',
      conditions: [{ field: 'order_weight', operator: '<', value: '' }],
      actions: {
        carrier: '',
        service_code: '',
        box_preset: '',
        require_signature: false,
        add_insurance: false,
        insurance_value_type: 'order_total',
        insurance_value: 0,
      },
    });
    setEditingRule(null);
  };

  const handleCreateFromTemplate = async (template: any) => {
    try {
      await automationRulesApi.create({
        name: template.name,
        description: template.description,
        priority: rules.length * 10,
        enabled: false,
        conditions: template.conditions,
        actions: template.actions,
      });
      await loadRules();
      setShowTemplates(false);
      alert(`Rule created: "${template.name}"\n\nThe rule is disabled by default. Enable it when ready.`);
    } catch (error: any) {
      alert(`Failed to create rule: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleSaveRule = async () => {
    // Validation
    if (!formData.name.trim()) {
      alert('Please enter a rule name');
      return;
    }

    if (formData.conditions.length === 0) {
      alert('Please add at least one condition');
      return;
    }

    if (formData.conditions.some(c => !c.value && c.value !== 0)) {
      alert('Please fill in all condition values');
      return;
    }

    try {
      const ruleData = {
        name: formData.name,
        description: formData.description || undefined,
        priority: formData.priority,
        enabled: formData.enabled,
        conditions: {
          [formData.conditionLogic]: formData.conditions,
        },
        actions: {
          ...formData.actions,
          carrier: formData.actions.carrier || undefined,
          service_code: formData.actions.service_code || undefined,
          box_preset: formData.actions.box_preset || undefined,
        },
      };

      if (editingRule) {
        await automationRulesApi.update(editingRule.id, ruleData);
        alert('Rule updated successfully');
      } else {
        await automationRulesApi.create(ruleData);
        alert('Rule created successfully');
      }

      await loadRules();
      setShowCreateForm(false);
      resetForm();
    } catch (error: any) {
      alert(`Failed to save rule: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleEditRule = (rule: AutomationRule) => {
    const conditionLogic = rule.conditions.all ? 'all' : 'any';
    const conditions = rule.conditions[conditionLogic] || [];

    setFormData({
      name: rule.name,
      description: rule.description || '',
      priority: rule.priority,
      enabled: rule.enabled,
      conditionLogic,
      conditions,
      actions: {
        carrier: rule.actions.carrier || '',
        service_code: rule.actions.service_code || '',
        box_preset: rule.actions.box_preset || '',
        require_signature: rule.actions.require_signature || false,
        add_insurance: rule.actions.add_insurance || false,
        insurance_value_type: rule.actions.insurance_value_type || 'order_total',
        insurance_value: rule.actions.insurance_value || 0,
      },
    });
    setEditingRule(rule);
    setShowCreateForm(true);
  };

  const handleToggleEnabled = async (ruleId: string, currentEnabled: boolean) => {
    try {
      await automationRulesApi.update(ruleId, { enabled: !currentEnabled });
      await loadRules();
    } catch (error: any) {
      alert(`Failed to toggle rule: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteRule = async (ruleId: string, ruleName: string) => {
    if (!window.confirm(`Delete rule "${ruleName}"?\n\nThis cannot be undone.`)) {
      return;
    }

    try {
      await automationRulesApi.delete(ruleId);
      await loadRules();
      alert('Rule deleted successfully');
    } catch (error: any) {
      alert(`Failed to delete rule: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleAddCondition = () => {
    setFormData({
      ...formData,
      conditions: [...formData.conditions, { field: 'order_weight', operator: '<', value: '' }],
    });
  };

  const handleUpdateCondition = (index: number, field: keyof Condition, value: any) => {
    const newConditions = [...formData.conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setFormData({ ...formData, conditions: newConditions });
  };

  const handleRemoveCondition = (index: number) => {
    if (formData.conditions.length === 1) {
      alert('At least one condition is required');
      return;
    }
    const newConditions = formData.conditions.filter((_, i) => i !== index);
    setFormData({ ...formData, conditions: newConditions });
  };

  const getServiceOptions = () => {
    const selectedCarrier = carriers.find(c => c.carrier_code === formData.actions.carrier);
    if (!selectedCarrier) return [];
    // Only return enabled methods
    return selectedCarrier.methods
      .filter(m => m.enabled)
      .map(m => ({ value: m.service_code, label: m.service_name }));
  };

  const getCarrierOptions = () => {
    return carriers.map(c => ({ value: c.carrier_code, label: c.carrier_name }));
  };

  const getFieldLabel = (fieldValue: string) => {
    return FIELDS.find(f => f.value === fieldValue)?.label || fieldValue;
  };

  const getOperatorLabel = (opValue: string) => {
    return OPERATORS.find(o => o.value === opValue)?.label || opValue;
  };

  const renderConditions = (conditions: any) => {
    if (conditions.all) {
      return conditions.all.map((c: any, i: number) => (
        <div key={i} className="text-sm text-slate-600">
          {i > 0 && <span className="text-xs font-semibold text-blue-600 mr-2">AND</span>}
          <span className="font-medium">{getFieldLabel(c.field)}</span>{' '}
          <span className="text-slate-400">{getOperatorLabel(c.operator)}</span>{' '}
          <span className="font-mono font-semibold text-slate-900">{c.value}</span>
        </div>
      ));
    }
    if (conditions.any) {
      return conditions.any.map((c: any, i: number) => (
        <div key={i} className="text-sm text-slate-600">
          {i > 0 && <span className="text-xs font-semibold text-purple-600 mr-2">OR</span>}
          <span className="font-medium">{getFieldLabel(c.field)}</span>{' '}
          <span className="text-slate-400">{getOperatorLabel(c.operator)}</span>{' '}
          <span className="font-mono font-semibold text-slate-900">{c.value}</span>
        </div>
      ));
    }
    return null;
  };

  const renderActions = (actions: any) => {
    const actionsList = [];
    if (actions.carrier) actionsList.push({ label: 'Carrier', value: actions.carrier.toUpperCase(), color: 'blue' });
    if (actions.service_code) actionsList.push({ label: 'Service', value: actions.service_code, color: 'blue' });
    if (actions.box_preset) actionsList.push({ label: 'Box', value: actions.box_preset, color: 'green' });
    if (actions.require_signature) actionsList.push({ label: 'Signature Required', value: 'Yes', color: 'orange' });
    if (actions.add_insurance) {
      const insuranceLabel = actions.insurance_value_type === 'order_total' 
        ? 'Order Total' 
        : actions.insurance_value 
          ? `$${actions.insurance_value}` 
          : 'Yes';
      actionsList.push({ label: 'Insurance', value: insuranceLabel, color: 'orange' });
    }

    return actionsList.map((action, i) => (
      <span key={i} className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium mr-2 mb-1 bg-${action.color}-100 text-${action.color}-700`}>
        <span className="font-semibold mr-1">{action.label}:</span> {action.value}
      </span>
    ));
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Automation Rules</h1>
          <p className="text-slate-600">
            Automatically select carriers, services, and boxes based on order criteria
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className="px-4 py-2 text-sm font-medium text-brand-blue bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            Use Template
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors"
          >
            + Create Custom Rule
          </button>
          {rules.length > 0 && (
            <button
              onClick={handleReapplyRules}
              disabled={reapplying}
              className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
            >
              {reapplying ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-orange-700 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Applying...
                </>
              ) : (
                'Apply to Existing Orders'
              )}
            </button>
          )}
        </div>

        {/* Templates Panel */}
        {showTemplates && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Rule Templates</h2>
              <button
                onClick={() => setShowTemplates(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template, index) => (
                <div
                  key={index}
                  onClick={() => handleCreateFromTemplate(template)}
                  className="border border-slate-200 rounded-lg p-4 hover:border-brand-blue hover:bg-blue-50 cursor-pointer transition-all"
                >
                  <h3 className="font-semibold text-slate-900 mb-1">{template.name}</h3>
                  <p className="text-xs text-slate-600 mb-3">{template.description}</p>
                  <div className="space-y-2">
                    <div className="text-xs">
                      <span className="font-medium text-slate-700">IF:</span>
                      <div className="ml-2 mt-1">{renderConditions(template.conditions)}</div>
                    </div>
                    <div className="text-xs">
                      <span className="font-medium text-slate-700">THEN:</span>
                      <div className="ml-2 mt-1">{renderActions(template.actions)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create/Edit Rule Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900">
                    {editingRule ? 'Edit Rule' : 'Create New Rule'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      resetForm();
                    }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-6 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Rule Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Small Orders to Georgia"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional description of what this rule does"
                      rows={2}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Priority
                      </label>
                      <input
                        type="number"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      />
                      <p className="text-xs text-slate-500 mt-1">Higher priority rules run first</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Status
                      </label>
                      <div className="flex items-center space-x-2 h-10">
                        <input
                          type="checkbox"
                          id="rule-enabled"
                          checked={formData.enabled}
                          onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                          className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                        />
                        <label htmlFor="rule-enabled" className="text-sm text-slate-700">
                          Enable this rule
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Conditions Section */}
                <div className="border-t border-slate-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-900">Conditions</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">Match:</span>
                      <select
                        value={formData.conditionLogic}
                        onChange={(e) => setFormData({ ...formData, conditionLogic: e.target.value as 'all' | 'any' })}
                        className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      >
                        <option value="all">ALL conditions</option>
                        <option value="any">ANY condition</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {formData.conditions.map((condition, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <select
                            value={condition.field}
                            onChange={(e) => handleUpdateCondition(index, 'field', e.target.value)}
                            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                          >
                            {FIELDS.map(field => (
                              <option key={field.value} value={field.value}>{field.label}</option>
                            ))}
                          </select>

                          <select
                            value={condition.operator}
                            onChange={(e) => handleUpdateCondition(index, 'operator', e.target.value)}
                            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                          >
                            {OPERATORS.map(op => (
                              <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                          </select>

                          <input
                            type="text"
                            value={condition.value}
                            onChange={(e) => handleUpdateCondition(index, 'value', e.target.value)}
                            placeholder="Value"
                            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                          />
                        </div>

                        <button
                          onClick={() => handleRemoveCondition(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove condition"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleAddCondition}
                    className="mt-3 px-4 py-2 text-sm font-medium text-brand-blue hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    + Add Condition
                  </button>
                </div>

                {/* Actions Section */}
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Actions</h3>
                  <p className="text-sm text-slate-600 mb-4">What should happen when conditions are met?</p>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Carrier
                        </label>
                        <select
                          value={formData.actions.carrier}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              actions: {
                                ...formData.actions,
                                carrier: e.target.value,
                                service_code: '', // Reset service when carrier changes
                              }
                            });
                          }}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        >
                          <option value="">Select carrier...</option>
                          {getCarrierOptions().map(carrier => (
                            <option key={carrier.value} value={carrier.value}>{carrier.label}</option>
                          ))}
                        </select>
                        {carriers.length === 0 && (
                          <p className="text-xs text-slate-500 mt-1">Loading carriers...</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Service
                        </label>
                        <select
                          value={formData.actions.service_code}
                          onChange={(e) => setFormData({
                            ...formData,
                            actions: { ...formData.actions, service_code: e.target.value }
                          })}
                          disabled={!formData.actions.carrier}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Select service...</option>
                          {getServiceOptions().map(service => (
                            <option key={service.value} value={service.value}>{service.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Box Preset
                      </label>
                      <select
                        value={formData.actions.box_preset}
                        onChange={(e) => setFormData({
                          ...formData,
                          actions: { ...formData.actions, box_preset: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      >
                        <option value="">Select box preset...</option>
                        {BOX_PRESETS.map(box => (
                          <option key={box.value} value={box.value}>{box.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="require-signature"
                          checked={formData.actions.require_signature}
                          onChange={(e) => setFormData({
                            ...formData,
                            actions: { ...formData.actions, require_signature: e.target.checked }
                          })}
                          className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                        />
                        <label htmlFor="require-signature" className="text-sm text-slate-700">
                          Require Signature
                        </label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="add-insurance"
                          checked={formData.actions.add_insurance}
                          onChange={(e) => setFormData({
                            ...formData,
                            actions: { ...formData.actions, add_insurance: e.target.checked }
                          })}
                          className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                        />
                        <label htmlFor="add-insurance" className="text-sm text-slate-700">
                          Add Insurance
                        </label>
                      </div>
                    </div>

                    {/* Insurance Value Options (shown when Add Insurance is checked) */}
                    {formData.actions.add_insurance && (
                      <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        {/* Insurance Provider Selection */}
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Insurance Provider
                        </label>
                        <div className="flex items-center gap-4 mb-4">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="insurance_provider"
                              value="parcelguard"
                              checked={formData.actions.insurance_provider === 'parcelguard'}
                              onChange={() => setFormData({
                                ...formData,
                                actions: { ...formData.actions, insurance_provider: 'parcelguard' }
                              })}
                              className="text-brand-blue focus:ring-brand-blue"
                            />
                            <span className="text-sm text-slate-700">ParcelGuard (~$0.99/100)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="insurance_provider"
                              value="carrier"
                              checked={formData.actions.insurance_provider === 'carrier'}
                              onChange={() => setFormData({
                                ...formData,
                                actions: { ...formData.actions, insurance_provider: 'carrier' }
                              })}
                              className="text-brand-blue focus:ring-brand-blue"
                            />
                            <span className="text-sm text-slate-700">Carrier Insurance (UPS/FedEx)</span>
                          </label>
                        </div>

                        {/* Insurance Value */}
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Insurance Value
                        </label>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="insurance_value_type"
                              value="order_total"
                              checked={formData.actions.insurance_value_type === 'order_total'}
                              onChange={() => setFormData({
                                ...formData,
                                actions: { ...formData.actions, insurance_value_type: 'order_total' }
                              })}
                              className="text-brand-blue focus:ring-brand-blue"
                            />
                            <span className="text-sm text-slate-700">Use Order Total</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="insurance_value_type"
                              value="fixed"
                              checked={formData.actions.insurance_value_type === 'fixed'}
                              onChange={() => setFormData({
                                ...formData,
                                actions: { ...formData.actions, insurance_value_type: 'fixed' }
                              })}
                              className="text-brand-blue focus:ring-brand-blue"
                            />
                            <span className="text-sm text-slate-700">Fixed Amount</span>
                          </label>
                        </div>
                        
                        {formData.actions.insurance_value_type === 'fixed' && (
                          <div className="mt-3">
                            <div className="flex items-center">
                              <span className="px-3 py-2 text-sm bg-slate-100 border border-r-0 border-slate-200 rounded-l-lg text-slate-500">$</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.actions.insurance_value || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  actions: { ...formData.actions, insurance_value: parseFloat(e.target.value) || 0 }
                                })}
                                placeholder="0.00"
                                className="w-32 px-3 py-2 text-sm border border-slate-200 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                              />
                            </div>
                          </div>
                        )}
                        
                        <p className="text-xs text-slate-500 mt-2">
                          {formData.actions.insurance_provider === 'parcelguard' ? 'ParcelGuard is typically cheaper than carrier insurance. ' : 'Carrier insurance uses UPS Declared Value or FedEx equivalent. '}
                          {formData.actions.insurance_value_type === 'order_total' 
                            ? 'Insurance will be set to the order total automatically when shipping.'
                            : 'Insurance will be set to the specified fixed amount.'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-4">
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      resetForm();
                    }}
                    className="px-6 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveRule}
                    className="px-6 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors"
                  >
                    {editingRule ? 'Update Rule' : 'Create Rule'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rules List */}
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-12 text-center">
            <p className="text-slate-600">Loading rules...</p>
          </div>
        ) : rules.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-12 text-center">
            <div className="text-6xl mb-4">⚡</div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No automation rules yet</h3>
            <p className="text-slate-600 mb-6">
              Create rules to automatically select carriers, services, and boxes based on order criteria
            </p>
            <button
              onClick={() => {
                resetForm();
                setShowCreateForm(true);
              }}
              className="px-6 py-3 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors"
            >
              Create Your First Rule
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {rules
              .sort((a, b) => b.priority - a.priority)
              .map((rule) => (
                <div key={rule.id} className="bg-white rounded-xl border border-slate-200 shadow-subtle hover:shadow-md transition-shadow">
                  {/* Rule Header */}
                  <div className="px-6 py-4 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-slate-900">{rule.name}</h3>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                          Priority: {rule.priority}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          rule.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {rule.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditRule(rule)}
                          className="px-3 py-1.5 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleEnabled(rule.id, rule.enabled)}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            rule.enabled
                              ? 'text-orange-600 border border-orange-600 hover:bg-orange-50'
                              : 'text-green-600 border border-green-600 hover:bg-green-50'
                          }`}
                        >
                          {rule.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleDeleteRule(rule.id, rule.name)}
                          className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {rule.description && (
                      <p className="text-sm text-slate-600 mt-2">{rule.description}</p>
                    )}
                  </div>

                  {/* Rule Details */}
                  <div className="px-6 py-4">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">IF</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                            {rule.conditions.all ? 'ALL' : 'ANY'}
                          </span>
                        </div>
                        <div className="space-y-1 pl-3 border-l-2 border-blue-200">
                          {renderConditions(rule.conditions)}
                        </div>
                      </div>

                      <div>
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">THEN</div>
                        <div className="pl-3 border-l-2 border-green-200">
                          {renderActions(rule.actions)}
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-6 text-sm text-slate-600">
                      <div>
                        <span className="font-medium">Times triggered:</span> {rule.times_triggered}
                      </div>
                      {rule.last_triggered_at && (
                        <div>
                          <span className="font-medium">Last triggered:</span>{' '}
                          {new Date(rule.last_triggered_at).toLocaleString()}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Created:</span>{' '}
                        {new Date(rule.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Info Panel */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">How Automation Rules Work</h3>
          <div className="space-y-2 text-sm text-blue-800">
            <p>
              <strong>Priority:</strong> Rules are evaluated in order from highest to lowest priority. The first matching rule is applied.
            </p>
            <p>
              <strong>Conditions:</strong> Set criteria that must be met for the rule to trigger. Use "ALL" to require all conditions, or "ANY" to require at least one.
            </p>
            <p>
              <strong>Actions:</strong> When conditions are met, the specified carrier, service, box, and options are automatically applied to the shipment.
            </p>
            <p>
              <strong>Templates:</strong> Get started quickly with pre-built rules for common shipping scenarios.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
